import { ReactElement, useEffect, useState } from 'react'

import { Button } from 'antd'
import classNames from 'classnames'
import { ErrorMessage, Form, Formik } from 'formik'
import { Trans, useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'
import { object, string } from 'yup'

import CodeInput from 'components/CodeInput/CodeInput'
import { SegmentEventName } from 'const/monitoring/segment'
import { resendCode, signInMfa } from 'features/Auth/authService'
import { useAnalytics } from 'features/Monitoring/Segment/useAnalytics'
import { useAuthDomainValue } from 'hooks'
import { getPath } from 'routing/pagePaths'
import { contactUsExternalUrl } from 'routing/urls'
import { AuthError, SubmitButton } from 'views/Auth/Shared'

import * as authStyles from '../styles.scss'

interface ChallengeParameters {
    CODE_DELIVERY_DESTINATION: string
}

interface Tokens {
    ChallengeName: string
    Session: string
    ChallengeParameters: ChallengeParameters
    ResponseMetadata: object
}

interface SmsCodeFormState {
    tokens: Tokens
    email: string
    password: string
}

const SmsCodeForm = (): ReactElement => {
    const { t } = useTranslation('account')
    const location = useLocation()
    const [hasRetried, setHasRetried] = useState(false)
    const smsChallengeContext = location.state as SmsCodeFormState | undefined
    const tokens = smsChallengeContext?.tokens
    const email = smsChallengeContext?.email
    const password = smsChallengeContext?.password

    const validationSchema = object().shape({
        smsCode: string()
            .required('Please enter the code we sent you.')
            .label(t('account:Sm.smsCode', 'smsCode')),
    })

    const isFetchingCode = useAuthDomainValue<boolean>('isFetchingCode')
    const { trackEvent, trackCtaClick } = useAnalytics()

    const triggerCTAEvent = (destination: string, text: string): void => {
        trackCtaClick({
            destination,
            text,
            type: 'button',
            location: 'MFA - Insert SMS Code',
        })
    }

    const handleResendClick = (e: { preventDefault: () => void }): void => {
        triggerCTAEvent('MFA - Insert SMS Code', 'Resend Code')
        e.preventDefault()

        if (!isFetchingCode) {
            resendCode({ email: email!, password: password! })
            setHasRetried(true)
        }
    }

    useEffect(() => {
        trackEvent(SegmentEventName.MFA_INSERT_SMS_CODE_STARTED, {
            text: 'MFA - Insert SMS Code Started',
        })
    }, [trackEvent])

    if (!tokens) {
        return <Navigate to={getPath('authPage')} />
    }

    return (
        <div>
            <div
                className={classNames(
                    authStyles.authFormTitleBlock,
                    authStyles['authFormTitleBlock--sms']
                )}
            >
                <h1>{t('account:SmsForm:title', 'Enter SMS Code')}</h1>
                <p className={authStyles.authFormSmsMessage}>
                    {t(
                        'account:SmsForm:message',
                        'We sent a 6-digit SMS code to your phone number: {{codeDeliveryDestination}}. Please enter it to complete authentication.',
                        {
                            codeDeliveryDestination:
                                tokens?.ChallengeParameters?.CODE_DELIVERY_DESTINATION?.replace(
                                    '+',
                                    ''
                                ),
                        }
                    )}
                </p>
            </div>
            <Formik
                initialValues={{ smsCode: '' }}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    triggerCTAEvent('Signed in - Landing page', 'Submit')
                    signInMfa({
                        email: email!,
                        password: password!,
                        tokens,
                        code: values.smsCode,
                    })
                }}
            >
                {({ setFieldValue }) => (
                    <Form autoComplete="off">
                        <div style={{ marginBottom: 8 }}>
                            <CodeInput
                                length={6}
                                onChange={(code) =>
                                    setFieldValue('smsCode', code)
                                }
                            />
                            <ErrorMessage
                                name="smsCode"
                                component="div"
                                className="fg-control-text is-error"
                            />
                        </div>
                        <SubmitButton>
                            {t('account:Sm.submitBtnText', 'Log In')}
                        </SubmitButton>
                    </Form>
                )}
            </Formik>

            <AuthError />

            <footer
                className={classNames(
                    authStyles.authFormFooter,
                    authStyles['authFormFooter--compressed']
                )}
            >
                {!hasRetried && (
                    <Trans i18nKey="account:SmsCodeForm.resendCodeLink">
                        Didn&rsquo;t receive a code?{' '}
                        <Button
                            type="link"
                            size="small"
                            onClick={handleResendClick}
                            style={{ padding: 0 }}
                        >
                            <span>Resend</span>
                        </Button>
                    </Trans>
                )}
                {hasRetried && isFetchingCode && (
                    <Trans i18nKey="account:SmsCodeForm.resendingCode">
                        <span>Resending...</span>
                    </Trans>
                )}
                {hasRetried && !isFetchingCode && (
                    <Trans i18nKey="account:SmsCodeForm.contacUs">
                        Still having issues?{' '}
                        <Button
                            href={contactUsExternalUrl(false)}
                            target="_blank"
                            rel="noopener noreferrer"
                            type="link"
                            size="small"
                            style={{ padding: 0 }}
                        >
                            <span>Contact us</span>
                        </Button>
                    </Trans>
                )}
            </footer>
        </div>
    )
}

export default SmsCodeForm
